import {
  Candidate,
  CANDIDATE_STATUS_LIST,
  CandidateStatus,
  TestScore,
  TestVerdict,
} from "../../types/recruiting";
import {
  findCountry,
  formatDateToddMMYYYY,
  parseCSVDate,
  setNestedProperty,
} from "../utils";

export const CSV_TEST_SCORE_EMAIL_FIELD = "E-mail";

export const CSV_CANDIDATE_MAPPING: Record<string, string> = {
  name: "name",
  surname: "surname",
  "maiden name": "maiden_name",
  phone: "phone",
  email: "email",
  "passport no": "passportId",
  region: "region",
  note: "note",
  interviewer: "interviewer",
  "preferred language": "preferredLanguage",
};

export const CSV_CANDIDATE_MAPPING_DATE_PROPERTIES: Record<string, string> = {
  "date of birth": "birth_date",
  "date of issue": "passport.issue_date",
  "date of expiry": "passport.expiry_date",
  "father - date of birth": "father.birthday",
  "mother - date of birth": "mother.birthday",
  "residency ownership since": "residency_date_since",
  "partner - date of birth": "partner.birthday",
};

export const CSV_CANDIDATE_MAPPING_BOOLEAN_PROPERTIES: Record<string, string> =
  {
    "privacy policy accepted": "privacy_policy_accepted",
    "has passport": "has_passport",
  };

export const CSV_CANDIDATE_MAPPING_COUNTRY_PROPERTIES: Record<string, string> =
  {
    nationality: "country",
  };

// export const CSV_ENGLISH_TEST_SCORE_MAPPING: Record<string, string> = {
//   start_date: "Дата начала",
//   finished: "Завершен",
//   score: "Балл",
//   maxScore: "Макс",
//   score_percentage: "(%)",
//   credibility: "Прокторинг - Оценка доверия",
//   verdict: "Прокторинг - Заключение",
// };

export const CSV_ENGLISH_TEST_SCORE_MAPPING: Record<string, string> = {
  email: "E-mail",
  name: "Name",
  surname: "Surname",
  start_date: "Start date and time",
  finished: "Completed",
  score: "Points score",
  maxScore: "Max points",
  score_percentage: "(%)",
  credibility: "Trust score",
  verdict: "Conclusion",
};

export const parseTestScore = (row: Record<string, string>) => {
  const startDate = row[CSV_ENGLISH_TEST_SCORE_MAPPING["start_date"]]
    ? parseDateTime(row[CSV_ENGLISH_TEST_SCORE_MAPPING["start_date"]])
    : "";
  const finished = row[CSV_ENGLISH_TEST_SCORE_MAPPING["finished"]] === "Да";
  const score = row[CSV_ENGLISH_TEST_SCORE_MAPPING["score"]]
    ? parseInt(row[CSV_ENGLISH_TEST_SCORE_MAPPING["score"]])
    : 0;
  const maxScore = row[CSV_ENGLISH_TEST_SCORE_MAPPING["maxScore"]]
    ? parseInt(row[CSV_ENGLISH_TEST_SCORE_MAPPING["maxScore"]])
    : 0;
  const scorePercentage = row[
    CSV_ENGLISH_TEST_SCORE_MAPPING["score_percentage"]
  ]
    ? parseFloat(
        row[CSV_ENGLISH_TEST_SCORE_MAPPING["score_percentage"]].replace(
          "%",
          ""
        ) || "0"
      )
    : 0;
  const credibility = row[CSV_ENGLISH_TEST_SCORE_MAPPING["credibility"]]
    ? parseInt(row[CSV_ENGLISH_TEST_SCORE_MAPPING["credibility"]])
    : 0;
  let verdict: TestVerdict = "none";
  switch (row[CSV_ENGLISH_TEST_SCORE_MAPPING["verdict"]] || "none") {
    case "Одобрено":
      verdict = "pass";
      break;
    case "Отклонено":
      verdict = "fail";
      break;
    default:
      verdict = "none";
  }

  const testScore: TestScore = {
    startDate,
    finished,
    score,
    maxScore,
    scorePercentage,
    credibility,
    verdict,
  };

  return testScore;
};

const parseDateTime = (date: string) => {
  if (!date) {
    return "";
  }
  try {
    // Remove extra spaces. this is a hack to fix the inaccurate input data
    const trimmedDate = date.trim().replace("  ", " ");
    // Split date and time parts
    const [datePart, timePart] = trimmedDate.split(" ");

    // Split date components (DD.MM.YYYY)
    const [day, month, year] = datePart.trim().split(".").map(Number);

    // Split time components (HH:mm:ss)
    const [hours, minutes, seconds] = timePart.trim().split(":").map(Number);

    // Check if any of the components are NaN or invalid
    if (
      isNaN(day) ||
      isNaN(month) ||
      isNaN(year) ||
      isNaN(hours) ||
      isNaN(minutes) ||
      isNaN(seconds) ||
      month < 1 ||
      month > 12 ||
      day < 1 ||
      day > 31 ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59 ||
      seconds < 0 ||
      seconds > 59
    ) {
      return "";
    }

    // JavaScript months are 0-based, so we subtract 1 from the month
    return new Date(
      year,
      month - 1,
      day,
      hours,
      minutes,
      seconds
    ).toISOString();
  } catch (error) {
    return "";
  }
};

export const getCandidateStatusFromLabel = (label: string) => {
  const res = CANDIDATE_STATUS_LIST.find(
    (status) => status.label.toLowerCase() === label.toLowerCase()
  );
  return res ? res.key : undefined;
};

export const parseCandidate = (
  dataRow: Record<string, string> | undefined | null,
  sponsorId: string,
  agentId: string,
  campaignId: string,
  initialStatus: CandidateStatus
) => {
  if (typeof dataRow === "object" && dataRow !== null) {
    // Create a new candidate from CSV data
    const newCandidate: Candidate = {
      name: "",
      surname: "",
      email: "",
      phone: "",
      sponsorId,
      agentId,
      campaignId,
      status: initialStatus,
    };

    const csvKeys = Object.keys(dataRow);
    csvKeys.forEach((key) => {
      key = key.trim();
      const csvValue = dataRow[key] ? dataRow[key].trim() : "";

      if (key.toLowerCase() === "status" && csvValue) {
        const status = getCandidateStatusFromLabel(csvValue);
        if (status) {
          newCandidate.status = status;
        }
      }

      // map standard fields
      const propertyName = CSV_CANDIDATE_MAPPING[key.toLowerCase()];
      if (propertyName) {
        setNestedProperty(newCandidate, propertyName, csvValue);
      }

      const booleanPropertyName =
        CSV_CANDIDATE_MAPPING_BOOLEAN_PROPERTIES[key.toLowerCase()];
      if (booleanPropertyName) {
        const booleanValue = csvValue.toLowerCase() === "yes" ? true : false;
        setNestedProperty(newCandidate, booleanPropertyName, booleanValue);
      }

      const datePropertyName =
        CSV_CANDIDATE_MAPPING_DATE_PROPERTIES[key.toLowerCase()];
      if (datePropertyName) {
        const date = parseCSVDate(dataRow[key]);
        setNestedProperty(
          newCandidate,
          datePropertyName,
          formatDateToddMMYYYY(date, "-")
        );
      }

      const countryPropertyName =
        CSV_CANDIDATE_MAPPING_COUNTRY_PROPERTIES[key.toLowerCase()];
      if (countryPropertyName) {
        const country = findCountry(dataRow[key].trim());
        if (country) {
          setNestedProperty(newCandidate, countryPropertyName, country);
        }
      }
    });

    return newCandidate;
  }
  return null;
};
